import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Copy, Link2, Loader2, ShieldCheck, Search, Check, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

const MODES = [
  ["copy", "Copy", "A separate copy lands in each tenant — later Eazy edits stay here.", Copy],
  ["link", "Live link", "Nothing is duplicated — your edits and credential changes flow through.", Link2],
];

// Item 24: Eazy staff provision a data source or dashboard into one tenant — or many at once.
export function ManagedCopyDialog({ open, onOpenChange, kind = "dataset", item, onDone }) {
  const isDash = kind === "dashboard";
  const [targets, setTargets] = useState(null);
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const [siteId, setSiteId] = useState("");
  const [mode, setMode] = useState("copy");
  const [level, setLevel] = useState("credentials");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMode("copy"); setLevel("credentials"); setBusy(false); setResults(null); setPayload(null); setQ("");
    setName(item?.name || "");
    api.get("/managed-sources/targets").then(({ data }) => {
      setTargets(data);
      setPicked(data.tenants?.[0]?.id ? [data.tenants[0].id] : []);
    }).catch((e) => {
      setTargets({ tenants: [], sites: [], levels: [] });
      toast.error(e?.response?.data?.detail || "Only Eazy staff can provision into a tenant");
    });
  }, [open, item?.name]);

  const tenants = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (targets?.tenants || []).filter((t) => !s || (t.name || "").toLowerCase().includes(s));
  }, [targets, q]);
  const sites = (targets?.sites || []).filter((s) => s.tenant_id === picked[0]);
  const many = picked.length > 1;

  useEffect(() => { setSiteId(""); }, [picked.length]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const go = async () => {
    if (!picked.length) { toast.error("Pick at least one tenant"); return; }
    setBusy(true);
    try {
      const body = {
        target_tenant_ids: picked,
        target_site_id: many ? null : siteId || null,
        mode, visibility_level: level, name: many ? null : name?.trim() || null,
      };
      const { data } = isDash
        ? await api.post("/managed-sources/dashboard", { ...body, dashboard_id: item.id })
        : await api.post("/managed-sources/copy", { ...body, dataset_id: item.id });
      const rows = data.results || [];
      if (rows.length > 1) {
        // Stay open so the per-tenant outcome can be read; the parent is told on Done.
        setResults(rows);
        setPayload(data);
        toast.success(`${data.succeeded} of ${rows.length} tenants provisioned`);
      } else {
        toast.success(mode === "link" ? `Live-linked into ${rows[0]?.tenant_name || "the tenant"}` : `Copied into ${rows[0]?.tenant_name || "the tenant"}`);
        onOpenChange(false);
        onDone?.(data);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not provision this into the tenant");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="managed-copy-dialog" className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[color:var(--brand-ocean)]" />
            Provision into {many ? `${picked.length} tenants` : "a tenant"}
          </DialogTitle>
          <DialogDescription>
            {isDash
              ? "Hand this dashboard to as many tenants as you like. Credentials never travel — they only ever see the data."
              : "Hand this data source to as many tenants as you like. The connection stays encrypted; hosts, users and passwords are never shown to a tenant."}
          </DialogDescription>
        </DialogHeader>

        {results ? (
          <div className="space-y-1.5 py-2" data-testid="managed-copy-results">
            {results.map((r) => (
              <div key={r.tenant_id} data-testid={`managed-copy-result-${r.tenant_id}`}
                className="flex items-center gap-2 rounded-lg border border-[color:var(--glass-border)] px-3 py-2 text-xs">
                {r.ok ? <Check className="w-3.5 h-3.5 text-[color:var(--success)] shrink-0" />
                  : <AlertTriangle className="w-3.5 h-3.5 text-[color:var(--danger)] shrink-0" />}
                <span className="text-[color:var(--ink)] flex-1 truncate">{r.tenant_name}</span>
                <span className="text-[color:var(--ink-3)]">{r.ok ? (r.mode === "link" ? "live-linked" : "copied") : r.error}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="rounded-lg bg-[color:var(--surface-2)] px-3 py-2 text-xs text-[color:var(--ink-2)]">
              {isDash ? "Dashboard" : "Data source"}: <b data-testid="managed-copy-item">{item?.name}</b>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Tenants <span className="text-[color:var(--ink-3)]">({picked.length} selected)</span></Label>
                <button type="button" data-testid="managed-copy-select-all"
                  onClick={() => setPicked(picked.length === tenants.length ? [] : tenants.map((t) => t.id))}
                  className="text-[11px] text-[color:var(--brand-ocean)] hover:underline">
                  {picked.length === tenants.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="relative mt-1.5">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-[color:var(--ink-3)]" />
                <Input data-testid="managed-copy-search" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search tenants…" className="pl-8 h-8 text-xs bg-[color:var(--surface)]" />
              </div>
              <div data-testid="managed-copy-tenant-list" className="mt-1.5 max-h-[150px] overflow-y-auto rounded-lg border border-[color:var(--glass-border)] divide-y divide-[color:var(--glass-border)]">
                {tenants.map((t) => (
                  <label key={t.id} data-testid={`managed-copy-tenant-${t.id}`}
                    className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-[color:var(--surface-2)]">
                    <Checkbox checked={picked.includes(t.id)} onCheckedChange={() => toggle(t.id)} />
                    <span className="text-xs text-[color:var(--ink)] flex-1 truncate">{t.name}</span>
                    {t.allow_tenant_data_sources === false && (
                      <span className="text-[9px] text-[color:var(--ink-3)]">Eazy-provisioned only</span>
                    )}
                  </label>
                ))}
                {!tenants.length && <p className="px-3 py-4 text-[11px] text-[color:var(--ink-3)]">No tenants match.</p>}
              </div>
            </div>

            {!many && sites.length > 1 && (
              <div>
                <Label className="text-xs">Site</Label>
                <Select value={siteId} onValueChange={setSiteId}>
                  <SelectTrigger data-testid="managed-copy-site" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue placeholder="Default site" /></SelectTrigger>
                  <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.is_default ? " (default)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs">How should it land?</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {MODES.map(([val, title, desc, Icon]) => (
                  <button key={val} type="button" data-testid={`managed-copy-mode-${val}`} onClick={() => setMode(val)}
                    className={cn("text-left rounded-xl border p-3 transition-colors",
                      mode === val
                        ? "border-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.06)]"
                        : "border-[color:var(--glass-border)] hover:bg-[color:var(--surface-2)]")}>
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--ink)]">
                      <Icon className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />{title}
                    </span>
                    <span className="block text-[11px] text-[color:var(--ink-3)] mt-1 leading-snug">{desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />What may the tenant see?</Label>
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger data-testid="managed-copy-level" className="mt-1.5 bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(targets?.levels || [{ value: "credentials", label: "Hide credentials" }]).map((l) => (
                    <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[color:var(--ink-3)] mt-1.5">
                {level === "credentials" && "The tenant sees the source name and type, never its host, user or password."}
                {level === "definition" && "Also hides the SQL / base query and table names from the tenant."}
                {level === "hidden" && "The tenant never sees the source at all — only the dataset built on it."}
              </p>
            </div>

            {!isDash && mode === "copy" && !many && (
              <div>
                <Label className="text-xs">Name in the tenant</Label>
                <Input data-testid="managed-copy-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 bg-[color:var(--surface)]" />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {results ? (
            <Button data-testid="managed-copy-close" onClick={() => { onOpenChange(false); onDone?.(payload); }} className="bg-[color:var(--brand-ocean)] text-white">Done</Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="managed-copy-confirm" onClick={go} disabled={busy || !picked.length} className="bg-[color:var(--brand-ocean)] text-white">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                  : mode === "link"
                    ? `Create live link${many ? `s · ${picked.length}` : ""}`
                    : `Copy into ${many ? `${picked.length} tenants` : "tenant"}`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
